import { NoImage } from "@assets/constant/index.constant";
import Tooltip from "@ui/external/components/tooltip";
import { ZoomImage } from "@ui/internal/components/formelement";
import { formatDateTime } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { BatchData, CourseBatchData } from "@utils/interface/journey.interface";
import { useSelectionStore } from "@utils/states/index.state";

const ColumnList = () => {
  const { handleAction } = useHandleAction({});
  const { clearSelection } = useSelectionStore();
  return [
    {
      name: "ID",

      cell: (row: any) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Created Date",
      accessKey: "created_date",
      cell: (row: any) => {
        return (
          <span className="font-semibold text-gray-700">
            {row.updated_date
              ? formatDateTime(row?.updated_date)
              : formatDateTime(row?.created_date)}
          </span>
        );
      },
      sortable: true,
    },
    {
      name: "Image",
      cell: (row: any) => (
        <div className="flex items-center gap-3">
          <ZoomImage>
            <img
              src={row?.feature_image || NoImage}
              alt="featured"
              onError={(e: any) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = NoImage;
              }}
              className="object-cover w-12 h-12 border rounded-md "
            />
          </ZoomImage>
        </div>
      ),
      sortable: false,
    },

    {
      name: "Title",
      cell: (row: any) => {
        return (
          <Tooltip description={row?.title || ""}>
            <div className="text-lg capitalize  max-w-[150px] w-fit truncate font-semibold text-gray-800">
              <span className="">{row?.title || ""}</span>
            </div>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      name: "Price",
      accessKey: "is_price",
      cell: (row: any) => (
        <span
          className={`px-2 py-1 rounded-md text-[12px] font-semibold ${
            row?.is_price
              ? "bg-[#1DA077]/10 text-[#1DA077]"
              : "bg-[#C33241]/10 text-[#C33241]"
          }`}
        >
          {row?.is_price ? "Shown" : "Hidden"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className="text-[#1DA077]"
            onClick={() => handleAction({ type: "page-edit", data: row.id })}
          >
            Edit
          </button>
          <button
            className="text-[#C33241]"
            onClick={() => {
              clearSelection();
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row.id },
              });
            }}
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};

const CategoryColumnList = () => {
  const { handleAction } = useHandleAction({});
  return [
    {
      name: "ID",
      width: "400px",

      cell: (row: any) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Name",
      width: "400px",
      cell: (row: any) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row.name}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start w-fit max-w-[150px] truncate">
                {row.name}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className="text-[#1DA077] cursor-pointer"
            onClick={() => handleAction({ type: "modal-edit", data: row.id })}
          >
            Edit
          </button>
          <button
            className="text-[#C33241] cursor-pointer"
            onClick={() =>
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row.id },
              })
            }
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};

const DurationColumnList = () => {
  const { handleAction } = useHandleAction({});
  return [
    {
      name: "ID",
      width: "400px",

      cell: (row: any) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Duration",
      width: "400px",
      cell: (row: any) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row.duration}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start  w-fit max-w-[150px] truncate">
                {row.duration}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: any) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className="text-[#1DA077] cursor-pointer"
            onClick={() => handleAction({ type: "modal-edit", data: row.id })}
          >
            Edit
          </button>
          <button
            className="text-[#C33241] cursor-pointer"
            onClick={() =>
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row.id },
              })
            }
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};

const BatchColumnList = () => {
  const { handleAction } = useHandleAction({});

  return [
    {
      name: "ID",
      width: "100px",

      cell: (row: BatchData) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Batch Name",
      width: "400px",
      cell: (row: CourseBatchData) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row?.name}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start  w-fit max-w-[350px] truncate">
                {row?.name}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },

    {
      name: "Actions",
      cell: (row: BatchData) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className="text-[#1DA077] cursor-pointer"
            onClick={() => handleAction({ type: "modal-edit", data: row?.id })}
          >
            Edit
          </button>
          <button
            className="text-[#C33241] cursor-pointer"
            onClick={() =>
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row?.id },
              })
            }
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};
const CourseBatchColumnList = () => {
  const { handleAction } = useHandleAction({});

  return [
    {
      name: "ID",
      width: "100px",

      cell: (row: BatchData) => (
        <span className="font-semibold text-gray-700">{row?.id}</span>
      ),
      sortable: true,
    },
    {
      name: "Course & Batch Name",
      width: "400px",
      cell: (row: CourseBatchData) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row.name}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start  w-fit max-w-[350px] truncate">
                {row.name}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Course",
      width: "200px",
      cell: (row: CourseBatchData) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row?.course?.title}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start  w-fit max-w-[150px] truncate">
                {row?.course?.title}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Batch",
      width: "200px",
      cell: (row: CourseBatchData) => (
        <div className="text-lg capitalize font-semibold text-gray-800">
          <Tooltip description={row?.batch?.name}>
            <div className="flex justify-start text-gray-800">
              <span className="text-start  w-fit max-w-[150px] truncate">
                {row?.batch?.name}
              </span>
            </div>
          </Tooltip>
        </div>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row: BatchData) => (
        <div className="flex items-center gap-3  cursor-pointer">
          <button
            className="text-[#1DA077] cursor-pointer"
            onClick={() => handleAction({ type: "modal-edit", data: row?.id })}
          >
            Edit
          </button>
          <button
            className="text-[#C33241] cursor-pointer"
            onClick={() =>
              handleAction({
                type: "confirm",
                data: { type: "delete", id: row?.id },
              })
            }
          >
            Delete
          </button>
        </div>
      ),
      sortable: false,
    },
  ];
};
export {
  BatchColumnList,
  CategoryColumnList,
  CourseBatchColumnList,
  ColumnList,
  DurationColumnList,
};
